import { BarChart } from '@mui/x-charts/BarChart';
import { axisClasses } from '@mui/x-charts/ChartsAxis';
import { useSelector, useDispatch } from 'react-redux';
import ATMPageHeader from 'src/components/UI/atoms/ATMPageHeader/ATMPageHeader';
import ATMDatePicker from 'src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker';
import { RootState } from 'src/redux/store';
import { setDateFilter } from 'src/redux/slices/FinanceDashboadSlice';

const IncomeExpensesCard = ({ percentage, title, isRevenue }: any) => {
    const arrowIcon = isRevenue ? '▲' : '▼';
    const arrowColor = isRevenue ? 'text-green-500' : 'text-red-500';

    return (
        <div className="bg-white p-4 flex items-center">
            <div className={`text-3xl font-bold ${arrowColor} mr-3`}>{arrowIcon}</div>
            <div>
                <div className={`text-2xl font-semibold ${arrowColor}`}>{percentage}</div>
                <div className="text-gray-600">{title}</div>
            </div>
        </div>
    );
};

const FinanceDashboard = ({ profitLossReportData }: any) => {
    const { dateFilter } = useSelector((state: RootState) => state.financeDashboad);
    const dispatch = useDispatch();

    const chartSetting = {
        width: 800,
        height: 400,
        margin: { left: 100 },
        sx: {
            [`.${axisClasses.left} .${axisClasses.label}`]: {
                transform: 'translate(-50px, 0)',
                fontSize: '14px',
                fontWeight: 'bold',
            },
        },
    };

    const valueFormatter = (value: number | null) => `₹${value}`;
    const finalAmount = parseInt(profitLossReportData?.totalIncome) || parseInt(profitLossReportData?.totalExpense)
        ? parseInt(profitLossReportData?.totalIncome) - parseInt(profitLossReportData?.totalExpense)
        : 0;

    return (
        <div className="bg-white min-h-screen">
            <div className='p-6'>
                <ATMPageHeader hideAddButton hideSearchBox pageTitle="Finance Dashboard" />
            </div>
            <div className='flex items-center justify-center'>
                <div className="w-full max-w-4xl p-6">
                    <div className="bg-white w-[700px]">
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <ATMDatePicker
                                    label="Start Month"
                                    size="small"
                                    format="MM/YYYY"
                                    views={['month']}
                                    value={dateFilter.startDate}
                                    onChange={(newValue) => {
                                        dispatch(setDateFilter({ ...dateFilter, startDate: newValue }));
                                    }}
                                />
                            </div>
                            <div className="flex-1">
                                <ATMDatePicker
                                    label="End Month"
                                    size="small"
                                    format="MM/YYYY"
                                    views={['month']}
                                    value={dateFilter.endDate}
                                    onChange={(newValue) => {
                                        dispatch(setDateFilter({ ...dateFilter, endDate: newValue }));
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white mt-6 flex flex-col items-center">
                        <div className="rounded-lg shadow-lg p-4 w-full">
                            <IncomeExpensesCard
                                percentage={finalAmount ? finalAmount : 0}
                                isRevenue={finalAmount >= 0}
                                title="Net Amount"
                            />
                            <div className="">
                                <div className="text-lg font-semibold">
                                    Total Income: <span className="text-green-600">₹ {profitLossReportData?.totalIncome || 0}</span>
                                </div>
                                <div className="text-lg font-semibold mt-2">
                                    Total Expense: <span className="text-red-600">₹ {profitLossReportData?.totalExpense || 0}</span>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 w-full shadow-lg rounded-lg">
                            <BarChart
                                dataset={profitLossReportData?.monthWiseTotal?.length ? profitLossReportData?.monthWiseTotal : []}
                                xAxis={[{ scaleType: 'band', dataKey: 'monthName' }]}
                                series={[
                                    { dataKey: 'totalIncome', label: 'Income', valueFormatter, color: '#10B981' }, // Green color
                                    { dataKey: 'totalExpense', label: 'Expense', valueFormatter, color: '#EF4444' }, // Red color
                                ]}
                                {...chartSetting}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default FinanceDashboard;
